const CANONICAL_OUTCOMES = ["fail", "marginal", "acceptable", "exemplary"];

const OUTCOME_ALIASES = {
  fail: "fail",
  deficient: "fail",
  critical: "fail",
  critical_concern: "fail",
  poor: "fail",
  no: "fail",
  not_supported: "fail",
  marginal: "marginal",
  needs_review: "marginal",
  needs_improvement: "marginal",
  unclear: "marginal",
  minor_issue: "marginal",
  not_applicable: "marginal",
  acceptable: "acceptable",
  pass: "acceptable",
  proficient: "acceptable",
  supported: "acceptable",
  yes: "acceptable",
  mostly: "acceptable",
  adequate: "acceptable",
  acceptable_with_minor_issues: "acceptable",
  partially_supported: "acceptable",
  exemplary: "exemplary",
  excellent: "exemplary",
  strong: "exemplary",
};

const OUTCOME_LABELS = {
  fail: "Fail",
  marginal: "Marginal",
  acceptable: "Acceptable",
  exemplary: "Exemplary",
};

const STATE_LABELS = {
  queued: "Queued",
  pending: "Pending",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
  uploaded: "Uploaded",
  extracting_text: "Extracting",
  text_extracted: "Text Extracted",
  analyzing: "Analyzing",
  analysis_complete: "Analysis Complete",
  requires_review: "Needs Review",
  draft_review: "Draft Review",
  needs_review: "Needs Review",
  pending_review: "Needs Review",
  reviewed: "Reviewed",
  ready: "Ready",
  awaiting_reviewer: "Awaiting Reviewer",
  released: "Released",
  resolved: "Resolved",
  auto_released: "Auto Released",
  pending_admin_review: "Pending Admin Review",
  approved: "Approved",
  rejected: "Rejected",
  exported: "Exported",
  not_reviewed: "Not Reviewed",
};

function normalizeToken(value) {
  return String(value || "").trim().toLowerCase();
}

export function normalizeCanonicalOutcome(value) {
  const token = normalizeToken(value);
  if (!token) return null;
  return OUTCOME_ALIASES[token] || null;
}

export function getOutcomeLabel(value) {
  const outcome = normalizeCanonicalOutcome(value);
  return outcome ? OUTCOME_LABELS[outcome] : null;
}

export function getStateLabel(value) {
  const token = normalizeToken(value);
  return STATE_LABELS[token] || (token ? token.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null);
}

export function getOutcomeColorScheme(value) {
  switch (normalizeCanonicalOutcome(value)) {
    case "exemplary":
      return "teal";
    case "acceptable":
      return "green";
    case "marginal":
      return "orange";
    case "fail":
      return "red";
    default:
      return "gray";
  }
}

export function getStateColorScheme(value, category = "state") {
  const token = normalizeToken(value);
  if (category === "training") {
    if (token === "approved") return "green";
    if (token === "exported") return "blue";
    if (token === "rejected") return "red";
    if (token === "pending_admin_review") return "purple";
  }
  if (category === "processing") {
    if (token === "completed" || token === "analysis_complete") return "green";
    if (token === "failed") return "red";
    if (token === "processing" || token === "analyzing" || token === "extracting_text") return "blue";
    if (token === "requires_review") return "orange";
    return "gray";
  }
  if (token === "reviewed" || token === "released" || token === "resolved" || token === "ready" || token === "completed") return "green";
  if (token === "needs_review" || token === "pending_review" || token === "awaiting_reviewer" || token === "draft_review") return "orange";
  if (token === "approved") return "green";
  if (token === "rejected" || token === "failed") return "red";
  return "gray";
}

function buildFacet(value, label, colorScheme) {
  if (!value) return null;
  return { value, label, colorScheme };
}

export function normalizeFisdapReviewPresentation(record = {}) {
  const humanReview = record?.structuredData?.humanReview || {};
  const aiOutcomeSnapshot =
    humanReview?.aiOutcome ||
    humanReview?.aiOutcomeSnapshot?.value ||
    humanReview?.aiOutcomeSnapshot ||
    null;
  const humanOutcome = humanReview?.humanOutcome || humanReview?.outcome || record?.hitlOverrideVerdict || null;
  const currentVerdict = record?.auditVerdict || null;
  const normalizedAiOutcome = normalizeCanonicalOutcome(aiOutcomeSnapshot) || (!humanOutcome ? normalizeCanonicalOutcome(currentVerdict) : null);
  const normalizedHumanOutcome = normalizeCanonicalOutcome(humanOutcome);
  const effectiveOutcome = normalizedHumanOutcome || normalizeCanonicalOutcome(currentVerdict);

  const processingValue =
    normalizeToken(record?.evaluationStatus) ||
    normalizeToken(record?.processingStatus) ||
    "pending";
  let reviewValue = "not_reviewed";
  if (record?.reviewedAt || humanReview?.reviewedAt) {
    reviewValue = "reviewed";
  } else if (humanReview?.reviewState === "draft_review" || humanReview?.status === "draft") {
    reviewValue = "draft_review";
  } else if (
    record?.requiresReview === true ||
    ["required", "needs_review", "indeterminate"].includes(normalizeToken(currentVerdict)) ||
    normalizeToken(record?.hitlStatus) === "pending_review"
  ) {
    reviewValue = "needs_review";
  } else if (processingValue === "processing" || processingValue === "pending") {
    reviewValue = "pending";
  } else {
    reviewValue = "ready";
  }

  const trainingValue = normalizeToken(
    humanReview?.trainingArtifactStatus ||
      humanReview?.trainingState ||
      record?.structuredData?.trainingArtifactStatus
  ) || null;

  return {
    serviceType: "fisdap",
    processingState: buildFacet(processingValue, getStateLabel(processingValue), getStateColorScheme(processingValue, "processing")),
    reviewState: buildFacet(reviewValue, getStateLabel(reviewValue), getStateColorScheme(reviewValue)),
    aiOutcome: normalizedAiOutcome
      ? buildFacet(normalizedAiOutcome, getOutcomeLabel(normalizedAiOutcome), getOutcomeColorScheme(normalizedAiOutcome))
      : null,
    humanOutcome: normalizedHumanOutcome
      ? buildFacet(normalizedHumanOutcome, getOutcomeLabel(normalizedHumanOutcome), getOutcomeColorScheme(normalizedHumanOutcome))
      : null,
    effectiveOutcome: effectiveOutcome
      ? buildFacet(effectiveOutcome, getOutcomeLabel(effectiveOutcome), getOutcomeColorScheme(effectiveOutcome))
      : null,
    trainingState: trainingValue
      ? buildFacet(trainingValue, getStateLabel(trainingValue), getStateColorScheme(trainingValue, "training"))
      : null,
  };
}

export function normalizeQaHumanReviewSummary(value = {}) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const outcome = normalizeCanonicalOutcome(value?.humanOutcome || value?.outcome || value?.effectiveOutcome);
  const aiOutcome = normalizeCanonicalOutcome(value?.aiOutcome || value?.aiOutcomeSnapshot?.value || value?.aiOutcomeSnapshot);
  return {
    ...value,
    humanOutcome: outcome,
    aiOutcome,
    effectiveOutcome: outcome || aiOutcome || null,
    reviewState: normalizeToken(value?.reviewState) || (outcome ? "reviewed" : "not_reviewed"),
    trainingArtifactStatus: normalizeToken(value?.trainingArtifactStatus || value?.trainingState) || null,
  };
}

export function normalizeQaReviewPresentation(chart = {}) {
  const humanReview = normalizeQaHumanReviewSummary(chart?.humanReviewSummary);
  const confidenceSummary = chart?.confidenceSummary || {};
  const aiOutcome = normalizeCanonicalOutcome(confidenceSummary?.qualityTier || confidenceSummary?.overallStatus);
  const humanOutcome = humanReview?.humanOutcome || null;
  const effectiveOutcome = humanOutcome || aiOutcome || null;
  const processingValue = normalizeToken(chart?.status) || "queued";

  let reviewValue = normalizeToken(humanReview?.reviewState);
  if (!reviewValue) {
    const reviewStatus = normalizeToken(chart?.reviewStatus);
    const portalStatus = normalizeToken(chart?.portalStatus);
    if (humanOutcome) {
      reviewValue = "reviewed";
    } else if (portalStatus === "released") {
      reviewValue = "released";
    } else if (reviewStatus === "needs_review" || portalStatus === "awaiting_reviewer") {
      reviewValue = "needs_review";
    } else if (reviewStatus === "completed" || reviewStatus === "resolved") {
      reviewValue = "ready";
    } else {
      reviewValue = reviewStatus || portalStatus || "pending";
    }
  }

  const trainingValue = humanReview?.trainingArtifactStatus || null;

  return {
    serviceType:
      chart?.sourceMetadata?.ingestionMode === "quickqa" || chart?.legacyQuickChartEvaluationId
        ? "quick_qa"
        : "live_qa",
    processingState: buildFacet(processingValue, getStateLabel(processingValue), getStateColorScheme(processingValue, "processing")),
    reviewState: buildFacet(reviewValue, getStateLabel(reviewValue), getStateColorScheme(reviewValue)),
    aiOutcome: aiOutcome ? buildFacet(aiOutcome, getOutcomeLabel(aiOutcome), getOutcomeColorScheme(aiOutcome)) : null,
    humanOutcome: humanOutcome ? buildFacet(humanOutcome, getOutcomeLabel(humanOutcome), getOutcomeColorScheme(humanOutcome)) : null,
    effectiveOutcome: effectiveOutcome ? buildFacet(effectiveOutcome, getOutcomeLabel(effectiveOutcome), getOutcomeColorScheme(effectiveOutcome)) : null,
    trainingState: trainingValue
      ? buildFacet(trainingValue, getStateLabel(trainingValue), getStateColorScheme(trainingValue, "training"))
      : null,
  };
}

export function buildStoredHumanReview({
  existing = {},
  aiOutcome,
  humanOutcome,
  reviewState = "reviewed",
  explanation = "",
  reviewNotes = "",
  reviewerId = null,
  reviewedAt = new Date().toISOString(),
  fieldOverrides = {},
  patientFlags = {},
  trainingArtifactStatus = null,
  serviceType = null,
}) {
  const normalizedHumanOutcome = normalizeCanonicalOutcome(humanOutcome);
  const normalizedAiOutcome = normalizeCanonicalOutcome(aiOutcome);

  return {
    ...existing,
    scaleVersion: "review-standard-v1",
    serviceType: serviceType || existing?.serviceType || null,
    aiOutcome: normalizedAiOutcome,
    aiOutcomeSnapshot: normalizedAiOutcome
      ? {
          value: normalizedAiOutcome,
          label: getOutcomeLabel(normalizedAiOutcome),
        }
      : existing?.aiOutcomeSnapshot || null,
    humanOutcome: normalizedHumanOutcome,
    effectiveOutcome: normalizedHumanOutcome || normalizedAiOutcome || null,
    reviewState: normalizeToken(reviewState) || (normalizedHumanOutcome ? "reviewed" : "draft_review"),
    explanation: String(explanation || existing?.explanation || "").trim(),
    reviewNotes: String(reviewNotes || existing?.reviewNotes || "").trim(),
    fieldOverrides: fieldOverrides && typeof fieldOverrides === "object" ? fieldOverrides : existing?.fieldOverrides || {},
    patientFlags: patientFlags && typeof patientFlags === "object" ? patientFlags : existing?.patientFlags || {},
    reviewedBy: reviewerId || existing?.reviewedBy || null,
    reviewedAt,
    trainingArtifactStatus: trainingArtifactStatus || existing?.trainingArtifactStatus || null,
  };
}

export { CANONICAL_OUTCOMES };
